import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { generateFakeEventData } from "../../../../utils/fakeEventData";
import { eventData } from "../../../pages/MyChamber/analyticsData";

// Define the type for our event data
export interface EventData {
  id: string;
  name: string;
  status: "upcoming" | "completed";
  ticketRevenue: number;
  sponsorshipRevenue: number;
  date: string;
  attendeeCount: number;
  location: string;
  eventType: string;
}

// Define sort directions
type SortDirection = "asc" | "desc";

// Define column keys
type ColumnKey =
  | "name"
  | "status"
  | "ticketRevenue"
  | "sponsorshipRevenue"
  | "date"
  | "attendeeCount"
  | "location"
  | "eventType";

const ManageEvents = () => {
  // State for event data
  const [events, setEvents] = useState<EventData[]>([]);

  // State for search query
  const [searchQuery, setSearchQuery] = useState("");

  // State for sorting
  const [sortColumn, setSortColumn] = useState<ColumnKey>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Load fake data on component mount
  useEffect(() => {
    const data = generateFakeEventData(20, 3);

    // Add event type to each event
    const enhancedData = data.map((event) => {
      const randomIndex = Math.floor(
        Math.random() * eventData.eventsByType.length
      );
      return {
        ...event,
        eventType: eventData.eventsByType[randomIndex].name,
      };
    });

    setEvents(enhancedData);
  }, []);

  // Handle search
  const handleSearchChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(target.value);
  };

  // Handle sort
  const handleRequestSort = (column: ColumnKey) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortColumn(column);
  };

  // Filter events based on search query
  const filteredEvents = events.filter((event) =>
    event.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort events based on current sort column and direction
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    // Always show upcoming events first regardless of sorting
    if (a.status === "upcoming" && b.status === "completed") return -1;
    if (a.status === "completed" && b.status === "upcoming") return 1;

    // If both events have the same status, sort by the selected column
    if (sortColumn === "name") {
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }

    if (sortColumn === "status") {
      return sortDirection === "asc"
        ? a.status.localeCompare(b.status)
        : b.status.localeCompare(a.status);
    }

    if (sortColumn === "ticketRevenue") {
      return sortDirection === "asc"
        ? a.ticketRevenue - b.ticketRevenue
        : b.ticketRevenue - a.ticketRevenue;
    }

    if (sortColumn === "sponsorshipRevenue") {
      return sortDirection === "asc"
        ? a.sponsorshipRevenue - b.sponsorshipRevenue
        : b.sponsorshipRevenue - a.sponsorshipRevenue;
    }

    if (sortColumn === "date") {
      return sortDirection === "asc"
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    }

    if (sortColumn === "attendeeCount") {
      return sortDirection === "asc"
        ? a.attendeeCount - b.attendeeCount
        : b.attendeeCount - a.attendeeCount;
    }

    if (sortColumn === "location") {
      return sortDirection === "asc"
        ? a.location.localeCompare(b.location)
        : b.location.localeCompare(a.location);
    }

    if (sortColumn === "eventType") {
      return sortDirection === "asc"
        ? a.eventType.localeCompare(b.eventType)
        : b.eventType.localeCompare(a.eventType);
    }

    return 0;
  });

  return (
    <div className="p-6">
      {/* Search bar */}
      <div className="mb-6 max-w-lg">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search events"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="text-gray-500" />
              </InputAdornment>
            ),
          }}
          className="bg-white"
        />
      </div>

      {/* Events table */}
      <TableContainer component={Paper} className="mb-8">
        <Table aria-label="events table">
          <TableHead className="bg-gray-50">
            <TableRow>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "name"}
                  direction={sortColumn === "name" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Event Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "eventType"}
                  direction={sortColumn === "eventType" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("eventType")}
                >
                  Event Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "status"}
                  direction={sortColumn === "status" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "date"}
                  direction={sortColumn === "date" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("date")}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "ticketRevenue"}
                  direction={
                    sortColumn === "ticketRevenue" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("ticketRevenue")}
                >
                  Ticket Revenue
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "sponsorshipRevenue"}
                  direction={
                    sortColumn === "sponsorshipRevenue" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("sponsorshipRevenue")}
                >
                  Sponsorship Revenue
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "attendeeCount"}
                  direction={
                    sortColumn === "attendeeCount" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("attendeeCount")}
                >
                  Attendees
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "location"}
                  direction={sortColumn === "location" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("location")}
                  IconComponent={KeyboardArrowDownIcon}
                >
                  Location
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEvents.map((event) => (
              <TableRow
                key={event.id}
                className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                  event.status === "upcoming" ? "bg-blue-50" : ""
                }`}
              >
                <TableCell component="th" scope="row">
                  {event.name}
                </TableCell>
                <TableCell>{event.eventType}</TableCell>
                <TableCell>
                  {event.status === "upcoming" ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Upcoming
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Completed
                    </span>
                  )}
                </TableCell>
                <TableCell>{event.date}</TableCell>
                <TableCell>
                  <span>
                    $
                    {event.ticketRevenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </TableCell>
                <TableCell>
                  <span>
                    $
                    {event.sponsorshipRevenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </TableCell>
                <TableCell>
                  <span>{event.attendeeCount}</span>
                </TableCell>
                <TableCell>{event.location}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ManageEvents;
